import { ReactComponent as ArrowUpIcon } from '@app/assets/arrow-up.svg';
import useLatestChapters from '@app/domains/chapter/hooks/useLatestChapters';
import Swiper from '@app/domains/common/components/Swiper';
import useInViewForPosthog from '@app/domains/common/hooks/useInViewForPosthog';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import HomeRecentlyUpdatedChapter from '@app/domains/home/components/HomeRecentlyUpdatedChapter';
import HomeSwiperController from '@app/domains/home/components/HomeSwiperController';
import useHomeMediaQuery from '@app/domains/home/hooks/useHomeMediaQuery';
import {
    HOME_SWIPER_PAGINATION_ACTIVE_BULLET_STYLE,
    HOME_SWIPER_PAGINATION_BULLET_STYLE,
} from '@app/domains/home/styles';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

export default function HomeRecentlyUpdatedChapters() {
    const { inView, ref } = useInViewForPosthog({
        event: HomeEvents.ViewCollection,
        triggerOnce: true,
    });

    const { data } = useLatestChapters({
        enabled: inView,
    });

    const chapters = useMemo(() => data?.items || [], [data?.items]);

    const { isMobile, isTablet, isUpMd } = useHomeMediaQuery();

    const slidesPerView = (() => {
        if (isMobile) return 1;
        if (isTablet) return 2;
        return 1;
    })();

    const spaceBetween = (() => {
        if (isMobile) return 12;
        if (isTablet) return 24;
        return 0;
    })();

    const chaptersPerSlide = (() => {
        if (isMobile) return 5;
        if (isTablet) return 5;
        return 15;
    })();

    const slideChapterArray = useMemo(
        () =>
            Array.from(Array(chapters.length / chaptersPerSlide), (_, i) =>
                chapters.slice(i * chaptersPerSlide, (i + 1) * chaptersPerSlide)
            ),
        [chapters, chaptersPerSlide]
    );

    return (
        <article ref={ref} data-posthog-params={JSON.stringify({ collectionSlot: HomeCollectionSlots.RecentUpdate })}>
            <div className="mb-20 flex items-center">
                <div className="font-set-b21 flex-1 text-gray-t1 md:font-set-b28">{RECENTLY_UPDATED_TITLE}</div>
                <Link to="/updates" className="font-set-m14 text-gray-600 hover:underline md:hidden">
                    View All
                </Link>
            </div>
            {chapters.length > 0 && (
                <div className="relative">
                    <Swiper
                        style={!isUpMd ? { overflow: 'visible' } : {}}
                        slidesPerView={slidesPerView}
                        slidesPerGroup={slidesPerView}
                        spaceBetween={spaceBetween}
                        navigation={isUpMd}
                        pagination={{
                            clickable: isUpMd,
                            bulletClass: HOME_SWIPER_PAGINATION_BULLET_STYLE,
                            bulletActiveClass: HOME_SWIPER_PAGINATION_ACTIVE_BULLET_STYLE,
                        }}
                    >
                        {slideChapterArray.map((slideChapterItems, slideIndex) => (
                            <Swiper.Slide key={slideIndex}>
                                {slideChapterItems.map((chapterItem, chapterIndex) => (
                                    <div
                                        key={chapterItem.entityId}
                                        data-posthog-params={JSON.stringify({
                                            itemSlot: slideIndex * chaptersPerSlide + chapterIndex,
                                        })}
                                    >
                                        <HomeRecentlyUpdatedChapter chapter={chapterItem} />
                                    </div>
                                ))}
                            </Swiper.Slide>
                        ))}
                        <div className="font-set-m14 absolute right-0 bottom-15 hidden text-right text-gray-600 md:block">
                            <Link className="hover:underline" to="/updates">
                                Click Here For All Updates
                            </Link>
                            <ArrowUpIcon className="inline-block h-12 w-12 rotate-90" />
                        </div>
                        <HomeSwiperController>
                            {isUpMd && <HomeSwiperController.PrevButton />}
                            <HomeSwiperController.Pages />
                            {isUpMd && <HomeSwiperController.NextButton />}
                        </HomeSwiperController>
                    </Swiper>
                </div>
            )}
        </article>
    );
}

const RECENTLY_UPDATED_TITLE = 'Most Recently Updated';
